/**
 * ZoneIcon.tsx — SINGLE SOURCE OF TRUTH for zone and emotion icons.
 *
 * ╔════════════════════════════════════════════════════════════════╗
 * ║  All zone/status icons MUST use this component.               ║
 * ║  Do NOT scatter zone icon logic across individual components.  ║
 * ║  Icons use react-icons/fi (Feather) — consistent across OS.  ║
 * ╚════════════════════════════════════════════════════════════════╝
 *
 * Usage:
 *   <ZoneIcon zone="Green Zone" size={20} />
 *   <EmotionIcon emotion="Happy" size={18} />
 */

import React from "react";
import {
  FiCheckCircle,
  FiDroplet,
  FiMinusCircle,
  FiAlertCircle,     // used in EmotionIcon (Anxious)
  FiAlertOctagon,
  FiTrendingDown,
  FiAlertTriangle,
  FiXCircle,
  FiSmile,
  FiMeh,
  FiFrown,
  FiHelpCircle,
  FiActivity,
  FiZap,
} from "react-icons/fi";
import { formatZoneName } from "../utils/zoneUtils";

// ── Zone Icon Configuration ────────────────────────────────────────────────────

interface ZoneIconConfig {
  Icon: React.ComponentType<{
    size?: number;
    color?: string;
    className?: string;
    "aria-label"?: string;
    "aria-hidden"?: boolean;
  }>;
  color: string;
  label: string;
}

const ZONE_ICON_MAP: Record<string, ZoneIconConfig> = {
  "Green Zone": {
    Icon: FiCheckCircle,
    color: "#10b981",
    label: "Green Zone — Excellent wellbeing",
  },
  "Blue Zone": {
    Icon: FiDroplet,          // calm, withdrawn, sadness
    color: "#3b82f6",
    label: "Blue Zone — Calm / withdrawn",
  },
  "Yellow Zone": {
    Icon: FiMinusCircle,
    color: "#f59e0b",
    label: "Yellow Zone — Moderate wellbeing",
  },
  "Orange Zone": {
    Icon: FiAlertOctagon,     // escalating caution
    color: "#f97316",
    label: "Orange Zone — Below average wellbeing",
  },
  "Brown Zone": {
    Icon: FiTrendingDown,     // low energy / declining
    color: "#b45309",
    label: "Brown Zone — Low wellbeing",
  },
  "Red Zone": {
    Icon: FiAlertTriangle,
    color: "#ef4444",
    label: "Red Zone — At risk",
  },
  "Dark Red": {
    Icon: FiXCircle,
    color: "#b91c1c",
    label: "Dark Red — Critical",
  },
};

const ZONE_ICON_FALLBACK: ZoneIconConfig = {
  Icon: FiHelpCircle,
  color: "#6b7280",
  label: "Unknown zone",
};

// ── ZoneIcon Component ─────────────────────────────────────────────────────────

interface ZoneIconProps {
  zone: string | null | undefined;
  /** Icon size in px. Defaults to 18. */
  size?: number;
  /** Override color (defaults to zone color). */
  color?: string;
  className?: string;
}

export const ZoneIcon: React.FC<ZoneIconProps> = ({
  zone,
  size = 18,
  color,
  className,
}) => {
  const key = formatZoneName(zone);
  const config = (key && ZONE_ICON_MAP[key]) || ZONE_ICON_FALLBACK;
  const { Icon, color: defaultColor, label } = config;

  return (
    <Icon
      size={size}
      color={color ?? defaultColor}
      aria-label={label}
      className={className}
    />
  );
};

// ── EmotionIcon Component ──────────────────────────────────────────────────────
// Replaces platform-dependent raw emoji faces (😊 😢 😐) with consistent SVG.

interface EmotionIconConfig {
  Icon: React.ComponentType<{
    size?: number;
    color?: string;
    className?: string;
    "aria-label"?: string;
    "aria-hidden"?: boolean;
  }>;
  color: string;
  label: string;
}

const EMOTION_ICON_MAP: Record<string, EmotionIconConfig> = {
  // Positive
  Happy:      { Icon: FiSmile,       color: "#10b981", label: "Happy" },
  Joy:        { Icon: FiSmile,       color: "#10b981", label: "Joy" },
  Excited:    { Icon: FiZap,         color: "#9b59b6", label: "Excited" },
  Calm:       { Icon: FiCheckCircle, color: "#10b981", label: "Calm" },
  // Neutral
  Complacent: { Icon: FiMeh,         color: "#f59e0b", label: "Complacent" },
  Neutral:    { Icon: FiMeh,         color: "#6b7280", label: "Neutral" },
  // Negative
  Anxious:    { Icon: FiAlertCircle, color: "#f59e0b", label: "Anxious" },
  Stressed:   { Icon: FiAlertTriangle, color: "#f97316", label: "Stressed" },
  Sad:        { Icon: FiFrown,       color: "#3b82f6", label: "Sad" },
  Angry:      { Icon: FiAlertTriangle, color: "#ef4444", label: "Angry" },
  // Activity
  Active:     { Icon: FiActivity,    color: "#1ecab8", label: "Active" },
};

const EMOTION_FALLBACK: EmotionIconConfig = {
  Icon: FiMeh,
  color: "#6b7280",
  label: "Unknown emotion",
};

interface EmotionIconProps {
  emotion: string | null | undefined;
  size?: number;
  color?: string;
  className?: string;
}

export const EmotionIcon: React.FC<EmotionIconProps> = ({
  emotion,
  size = 18,
  color,
  className,
}) => {
  const key = emotion?.trim() ?? "";
  const config = EMOTION_ICON_MAP[key] ?? EMOTION_FALLBACK;
  const { Icon, color: defaultColor, label } = config;

  return (
    <Icon
      size={size}
      color={color ?? defaultColor}
      aria-label={label}
      className={className}
    />
  );
};

// Re-export config for external use (e.g. legend rendering)
export { ZONE_ICON_MAP, ZONE_ICON_FALLBACK };
export type { ZoneIconConfig };
